import DataTable from '@components/Shared/DataTable/DataTable';
import { type BadgeTone, rowHeight, tableText } from '@components/Shared/DataTable/DataTable.styles';
import { DateCell, StatusBadge } from '@components/Shared/DataTable/TableCells';
import {
  BulkAction, BulkBar, BulkDivider, TableBar, TableSearch, TableSegments
} from '@components/Shared/DataTable/TableToolbar';
import {
  type DataTableFeatures, tableAddress, useDataTable, useTableParams
} from '@components/Shared/DataTable/useDataTable';
import ConfirmDialog from '@components/Shared/Dialog/ConfirmDialog';
import { formDialogTextStyles } from '@components/Shared/Dialog/formDialog.styles';
import Header from '@components/Shared/Layout/Header';
import { menuDangerItemStyles, menuItemStyles, menuPaperStyles } from '@components/Shared/Menu/menu.styles';
import DeleteUserDialog from '@components/Users/DeleteUserDialog';
import DeleteUsersDialog from '@components/Users/DeleteUsersDialog';
import UserAvatar from '@components/Users/UserAvatar';
import UserFormModal from '@components/Users/UserFormModal';
import { useSsoAccountActions } from '@components/Users/useSsoAccountActions';
import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import { Add } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box, Button, Divider, IconButton, ListItemIcon, Menu, MenuItem, Paper, Tooltip, Typography
} from '@mui/material';
import {
  useDeleteUserMutation, useGetUserPageQuery, useUpdateUserMutation, type User, userSortKeys, type UserTeam
} from '@store/api/userEndpoints';
import { useAppDispatch, useAppSelector, usePermission } from '@store/hooks';
import { useDebounce } from '@store/hooks/useDebounce';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { createColumnHelper } from '@tanstack/react-table';
import { brand, colorSchemes } from '@theme/primitives';
import {
  ArrowLeftRightIcon, LinkIcon, PencilIcon, Trash2Icon, UnlinkIcon, UserCheckIcon, UserXIcon
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { LockIcon, RefreshIcon } from 'react-line-awesome';
import { generatePath, NavigationType, Link as RouterLink, useLocation, useNavigationType } from 'react-router-dom';

/**
 * How an account stands: suspended comes before the rest, since such an account cannot sign in
 * at all; otherwise how it stands with single sign-on. The server sorts the column in this
 * order (`accountStateRank` in `user.service.ts`).
 */
type AccountState = 'suspended' | 'managed' | 'linked' | 'blocked' | 'local';

// An account handed to single sign-on reads as managed by it whatever the settings say now; the
// lock says whether the settings do govern it, and so whether nobody here may change it.
const stateOf = (row: User): AccountState => {
  if (row.isActive === false) return 'suspended';
  if (row.ssoLinked && row.managedBy === 'sso') return 'managed';
  if (row.ssoLinked) return 'linked';
  if (row.ssoLinkBlocked) return 'blocked';

  return 'local';
};

/** The short label in the column, the full sentence on hover where the label needs one, and its colour. */
const accountStates: Record<AccountState, { label: string; title?: string; tone: BadgeTone }> = {
  suspended: { label: 'User_Suspended', tone: 'danger' },
  managed: { label: 'User_SsoState_Managed', title: 'User_Sso_Managed', tone: 'success' },
  linked: { label: 'User_SsoState_Linked', title: 'User_Sso_Linked', tone: 'info' },
  blocked: { label: 'User_SsoState_Blocked', title: 'User_Sso_Blocked', tone: 'warning' },
  local: { label: 'User_SsoState_Local', title: 'User_Sso_NotLinked', tone: 'neutral' },
};

/** The statuses the segments cut the list by; none in the address is every account. */
const segmentOf = (value: string | null) => (value === 'active' || value === 'suspended' ? value : 'all');

/** The address's search, cut where the field and the server stop: a longer one, edited by hand, is refused. */
const searchOf = (params: URLSearchParams) => (params.get('q') ?? '').slice(0, 100);

/** Only an account tied to an identity can be handed to single sign-on or back. */
const canHandOver = (row: User) => Boolean(row.ssoLinked);

const helper = createColumnHelper<DataTableFeatures, User>();

/**
 * Who the account is: its name and username. A suspended account's name stands back, as its row
 * does (`isMuted` on the table); the status column says why.
 */
const PersonCell = ({ user }: { user: User }) => {
  const suspended = user.isActive === false;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <UserAvatar name={user.name} size={28} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: 13, fontWeight: 500, lineHeight: '20px', color: suspended ? tableText.secondary : tableText.primary }}
        >
          {user.name}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, lineHeight: '16px', color: tableText.tertiary }}>
          @{user.username}
        </Typography>
      </Box>
    </Box>
  );
};

/** The first two teams by name and how many more; every team with its role shows over the cell. */
const TeamsCell = ({ teams }: { teams: UserTeam[] }) => {
  const { t, currentLanguage } = useLocalization();
  const { can } = usePermission();

  if (teams.length === 0) return null;

  const sorted = [...teams].sort((a, b) => a.name.localeCompare(b.name, currentLanguage));

  return (
    <Tooltip
      placement="bottom-start"
      // Opens once the pointer rests, not on every row it passes on its way down the list; stays
      // open a moment after it leaves the cell, so the pointer can reach a team in the list.
      enterDelay={300}
      enterNextDelay={300}
      leaveDelay={150}
      // The list is drawn on a paper of its own, so it looks as the row's menu does and not as a
      // tooltip. It stands 4px off the cell: MUI puts its 14px on the placement's own class,
      // which only `!important` outweighs.
      slotProps={{ tooltip: { sx: { p: 0, my: '4px !important', maxWidth: 'none', backgroundColor: 'transparent' } } }}
      title={
        <Paper sx={teamListStyles}>
          {sorted.map((team) => {
            const row = (
              <>
                <span>{team.name}</span>
                <Box component="span" sx={{ fontSize: 12, color: tableText.tertiary }}>
                  {t(`Team_Role_${team.role.capitalize()}`)}
                </Box>
              </>
            );

            // A team the reader may open is a way to its page.
            return can('team:get', team.id) ? (
              <Box
                key={team.id}
                component={RouterLink}
                to={generatePath(ROUTE_PATHS.TEAM_DETAIL, { teamId: team.id })}
                sx={[teamRowStyles, teamLinkStyles]}
              >
                {row}
              </Box>
            ) : (
              <Box key={team.id} sx={teamRowStyles}>{row}</Box>
            );
          })}
        </Paper>
      }
    >
      {/* As tall as the row, so the list opens wherever the cell is pointed at, not only over its words. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, height: rowHeight }}>
        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sorted.slice(0, 2).map((team) => team.name).join(', ')}
        </Box>
        {sorted.length > 2 && (
          <Box component="span" sx={{ flexShrink: 0, fontSize: 12, color: tableText.tertiary }}>+{sorted.length - 2}</Box>
        )}
      </Box>
    </Tooltip>
  );
};

const UsersPage = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // The account the delete dialog is about, kept until the dialog has faded out so its content
  // stays put, and let go then; the openings are counted so that each one mounts the dialog anew,
  // with nothing chosen, even one made while the last is still fading.
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteOpenings, setDeleteOpenings] = useState(0);
  const [formModal, setFormModal] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  // What a bulk action asks about: kept apart from whether its dialog is open, so the count it
  // names stays put while the dialog closes and the selection is let go.
  const [bulkAction, setBulkAction] = useState<'suspend' | 'delete' | null>(null);
  const [bulkTargets, setBulkTargets] = useState<User[]>([]);
  const dispatch = useAppDispatch();
  const [deleteUser] = useDeleteUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const { t, currentLanguage } = useLocalization();
  const { user } = useAppSelector(state => state.auth);
  const { can } = usePermission();
  // How accounts stand with single sign-on is the sysadmin's, as the settings are.
  const canManageSso = can('sso_config:put');

  // What is listed is in the address, so a link opens the list as it was. The search is typed
  // into a state of its own: the router applies a change of the address in a transition, and a
  // field that takes its text from there loses keys and its caret.
  const { params, setParams } = useTableParams();
  const [search, setSearch] = useState(() => searchOf(params));
  const location = useLocation();
  const navigationType = useNavigationType();
  // An address the page did not write itself (the menu's link to it, Back) brings its own
  // search; the page's own writes replace the entry and are already in the field.
  useEffect(() => {
    if (navigationType !== NavigationType.Replace) setSearch(searchOf(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new address, not each key of it
  }, [location.key]);
  const segment = segmentOf(params.get('status'));

  // The server searches, sorts and pages: by name, username or e-mail, since a sysadmin looking
  // for an account single sign-on found often has only the address the provider gave. What it
  // is asked is the address's search, which the field writes with the first page once the
  // typing pauses.
  const sentSearch = searchOf(params);
  const typedSearch = useDebounce(search.trim(), 300);
  const address = tableAddress(params, userSortKeys, 'name');
  const { data: userPage, currentData, isLoading, isError, refetch, isFetching } = useGetUserPageQuery({
    page: address.pageIndex + 1,
    limit: address.pageSize,
    search: sentSearch || undefined,
    status: segment === 'all' ? undefined : segment,
    sort: address.sort,
  }, {
    // Not asked by whoever may not list the accounts, such as a team's manager who typed the
    // address: the layout sends them home, and the refusal showed as an error on the way.
    skip: !can('user:get'),
  });
  // The page last answered stays on screen under the thin bar while the next is read.
  const rows = userPage?.data ?? [];
  // What the search leaves, whatever the segment: what the segments count, once it is known.
  const counts = userPage?.meta.counts;
  // The column only says something once an account is suspended, single sign-on has reached one,
  // or one without a password waits for its first sign-in through it.
  // It stands there before that too, with a dash, so the columns do not move when the answer lands.
  const showsStatus = userPage?.meta.showsStatus ?? false;

  // The server sorts every column; a column names what the address and the request call it.
  const columns = useMemo(() => helper.columns([
    helper.accessor('name', {
      header: t('Common_Name'),
      meta: { sticky: 'start' },
      cell: (info) => <PersonCell user={info.row.original} />,
    }),
    helper.accessor('email', {
      header: t('User_Email'),
      meta: { width: 240 },
      cell: (info) => info.getValue() ? <span title={info.getValue() ?? undefined}>{info.getValue()}</span> : null,
    }),
    helper.accessor('role', {
      header: t('Common_Role'),
      meta: { width: 150 },
      // Most accounts are users: the colour goes to the few that are more.
      cell: (info) => (
        <StatusBadge
          label={t(`User_Role_${info.getValue().capitalize()}`)}
          tone={info.getValue() === 'admin' ? 'info' : 'neutral'}
        />
      ),
    }),
    helper.accessor((row) => stateOf(row), {
      id: 'status',
      header: t('Common_Status'),
      meta: { width: 200 },
      cell: (info) => {
        if (!showsStatus) return <Box component="span" sx={{ color: tableText.tertiary }}>—</Box>;

        const state = info.getValue();
        const { label, title, tone } = accountStates[state];

        return (
          <StatusBadge
            dot
            label={t(label)}
            // Two states say more than their label: handed over while single sign-on places nobody
            // (the admins manage it until it does, and only the missing lock shows that), and no
            // password while not tied, where single sign-on is the only way in.
            title={state === 'managed' && !info.row.original.ssoGoverned ? t('User_SsoChange_Inactive')
              : state === 'local' && info.row.original.hasPassword === false ? t('User_Sso_Waiting')
                : title && t(title)}
            tone={tone}
            icon={info.row.original.ssoGoverned ? <LockIcon aria-hidden /> : undefined}
          />
        );
      },
    }),
    helper.display({
      id: 'teams',
      header: t('Common_Teams'),
      meta: { width: 260 },
      cell: (info) => <TeamsCell teams={info.row.original.teams ?? []} />,
    }),
    helper.accessor('lastLoginAt', {
      header: t('User_LastSignIn'),
      meta: { width: 150 },
      sortDescFirst: true,
      // Sign-ins were not recorded before this column was, so an account without one may well
      // have signed in: a dash, not "Never".
      cell: (info) => <DateCell value={info.getValue()} empty="—" />,
    }),
    helper.accessor('createdAt', {
      header: t('Common_Created'),
      meta: { width: 150 },
      sortDescFirst: true,
      cell: (info) => <DateCell value={info.getValue()} />,
    }),
    helper.display({
      id: 'actions',
      header: '',
      meta: { width: 64, align: 'end', sticky: 'end' },
      // Nobody acts on their own account from here.
      cell: (info) => user?.id !== info.row.original.id ? (
        <IconButton
          size="small"
          aria-label={t('DataTable_RowActions', [info.row.original.name])}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            setSelectedUser(info.row.original);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ) : null,
    }),
  // `t` stays the same function in every language, so the language names the columns' words.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [t, currentLanguage, showsStatus, user?.id]);

  const table = useDataTable({
    columns,
    data: rows,
    getRowId: (row) => row.id,
    defaultSort: 'name',
    // Every account but the reader's own, which nobody acts on from here.
    canSelect: (row) => row.original.id !== user?.id,
    // The page shown is the one answered for the address now; while it is on its way, the
    // address's own.
    server: {
      sortable: userSortKeys,
      rowCount: userPage?.meta.total,
      pageIndex: currentData ? currentData.meta.page - 1 : undefined,
    },
  });

  const clearSelection = () => table.resetRowSelection(true);
  const ssoActions = useSsoAccountActions({ onDone: clearSelection });

  // Only rows that can still be picked: a row stays in the selection after it stops being
  // pickable (taken off single sign-on, say), and the bulk buttons then counted it and did
  // nothing for it.
  const selectedUsers = table.getSelectedRowModel().rows.filter((row) => row.getCanSelect()).map((row) => row.original);
  const activeSelected = selectedUsers.filter((selected) => selected.isActive !== false);
  const managedBySso = selectedUsers.filter((selected) => selected.managedBy === 'sso');
  const managedLocally = selectedUsers.filter((selected) => selected.managedBy !== 'sso');

  const askBulk = (action: 'suspend' | 'delete', targets: User[]) => {
    setBulkTargets(targets);
    setBulkAction(action);
  };

  /**
   * One request an account, side by side. A refusal the server explains says why on its own, as a
   * single one does; what went through is said once, and so is what did not, which covers a
   * request that failed without a word (a dropped connection, the rate limit). What did not go
   * through stays picked, so it can be tried again.
   */
  const runForEach = async (run: (target: User) => Promise<boolean>, doneKey: string, failedKey: string) => {
    const results = await Promise.all(bulkTargets.map(async (target) => ({ target, done: await run(target) })));
    const failed = results.filter((result) => !result.done).map((result) => result.target);
    const done = results.length - failed.length;

    if (done > 0) dispatch(showSnackbar({ severity: 'success', message: t(doneKey, { count: done }) }));
    if (failed.length > 0) {
      dispatch(showSnackbar({ severity: 'warning', message: t(failedKey, { count: failed.length }) }));
    }
    table.setRowSelection(Object.fromEntries(failed.map((target) => [target.id, true])));
  };

  const suspendTargets = () => runForEach(
    async (target) => !(await updateUser({ id: target.id, isActive: false, showSuccess: false })).error,
    'User_SuspendMany_Done',
    'User_BulkFailed'
  );

  const deleteTargets = () => runForEach(
    async (target) => !(await deleteUser({ id: target.id, showSuccess: false })).error,
    'User_DeleteMany_Done',
    'User_DeleteMany_Failed'
  );

  /** A change of what is listed goes back to the first page, and lets go of what was picked. */
  const setQuery = (values: Record<string, string | null>) => {
    setParams({ ...values, page: null });
    clearSelection();
  };

  // Only a change of what was typed is written: an address the page did not write (Back) brings
  // its search into the field, and the pause then lands on what the address already holds.
  useEffect(() => {
    if (typedSearch !== sentSearch) setQuery({ q: typedSearch || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- what was typed, not the address it goes to
  }, [typedSearch]);

  /** Every account again: no search, every status. */
  const showAll = () => {
    setSearch('');
    setQuery({ q: null, status: null });
  };

  const handleMenuClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    unfocusUser(!!reason);
  };

  const unfocusUser = (cleanSelectedUser: boolean = false) => {
    setAnchorEl(null);
    if (cleanSelectedUser) setSelectedUser(null);
  };

  const handleEdit = () => {
    setFormModal(true);
    unfocusUser();
  };

  const handleDelete = () => {
    if (selectedUser) {
      setDeleteTarget(selectedUser);
      setDeleteOpenings((count) => count + 1);
      setDialogOpen(true);
    }
    unfocusUser(true);
  };

  // Suspending is asked about first, since it signs the person out everywhere; letting them in
  // again is not.
  const handleSuspension = () => {
    if (selectedUser?.isActive === false) {
      void updateUser({ id: selectedUser.id, isActive: true });
      unfocusUser(true);
    } else {
      setSuspendOpen(true);
      unfocusUser();
    }
  };

  const handleSuspendClose = () => {
    setSuspendOpen(false);
    setSelectedUser(null);
  };

  const handleSsoAction = (action: (target: User) => void) => {
    if (selectedUser) action(selectedUser);
    unfocusUser(true);
  };

  const handleUserFormClose = () => {
    setFormModal(false);
    setSelectedUser(null);
  };

  const toolbar = selectedUsers.length > 0 ? (
    <BulkBar
      count={selectedUsers.length}
      available={rows.filter((row) => row.id !== user?.id).length}
      onSelectAll={() => table.toggleAllRowsSelected(true)}
      onClear={clearSelection}
    >
      {/* Offered only while every account picked can be handed over, and as the row's menu offers
          it: handing over what the admins manage, handing back what single sign-on does. */}
      {canManageSso && selectedUsers.every(canHandOver) && (
        <>
          {managedLocally.length > 0 && (
            <BulkAction icon={<ArrowLeftRightIcon size={16} />} onClick={() => ssoActions.setManagement(managedLocally, 'sso')}>
              {t('User_SsoHandOver')}
            </BulkAction>
          )}
          {managedBySso.length > 0 && (
            <BulkAction icon={<ArrowLeftRightIcon size={16} />} onClick={() => ssoActions.setManagement(managedBySso, 'local')}>
              {t('User_SsoHandBack')}
            </BulkAction>
          )}
          <BulkDivider />
        </>
      )}
      {activeSelected.length > 0 && (
        <BulkAction icon={<UserXIcon size={16} />} onClick={() => askBulk('suspend', activeSelected)}>
          {t('User_Suspend')}
        </BulkAction>
      )}
      <BulkAction icon={<Trash2Icon size={16} />} danger onClick={() => askBulk('delete', selectedUsers)}>
        {t('Common_Delete')}
      </BulkAction>
    </BulkBar>
  ) : (
    <TableBar>
      <TableSearch value={search} placeholder={t('User_Search')} maxLength={100} onChange={setSearch} />
      <TableSegments
        value={segment}
        onChange={(value) => setQuery({ status: value === 'all' ? null : value })}
        segments={[
          { value: 'all', label: t('DataTable_All'), count: counts?.all },
          { value: 'active', label: t('User_Active'), count: counts?.active },
          { value: 'suspended', label: t('User_Suspended'), count: counts?.suspended },
        ]}
      />
      <IconButton size="small" onClick={refetch} disabled={isLoading || isFetching} aria-label={t('DataTable_Refresh')}>
        <RefreshIcon />
      </IconButton>
    </TableBar>
  );

  return (
    <div style={containerStyles}>
      {/* The page's one action stands on the line of its name; the search is the table's. */}
      <Header>
        <Button
          variant="contained"
          data-testid="new-user-button"
          aria-label={t('User_New')}
          startIcon={<Add />}
          onClick={() => { setSelectedUser(null); setFormModal(true); }}
          sx={newButtonStyles}
        >
          {t('User_New')}
        </Button>
      </Header>
      <DataTable
        table={table}
        label={t('Common_Users')}
        toolbar={toolbar}
        rowName={(row) => row.name}
        // Placeholders while nothing has been read and a read is on its way, a retry after a failed
        // first read included: the empty message would say there is nothing.
        isLoading={isFetching && !userPage}
        // A search the address has not taken yet counts as read again, so no result is not said too soon.
        isFetching={isFetching || search.trim() !== sentSearch}
        // A read that failed says so, rather than leaving the page before it under its number, or
        // the rows it could not read again (a refresh, the list after a delete) as if they were current.
        isError={isError}
        onRetry={refetch}
        isMuted={(row) => row.isActive === false}
        // The search is named only while one is in force; without it, no accounts at all is told
        // apart from none in the segment.
        empty={sentSearch ? (
          <>
            {t('DataTable_NoResults')}
            <Button size="small" variant="outlined" onClick={showAll} sx={{ textTransform: 'none' }}>
              {t('DataTable_ShowAll')}
            </Button>
          </>
        ) : t(counts?.all === 0 ? 'User_NoUsers' : 'User_NoneHere')}
      />
      {/* The Folders page's row menu, in its size and look: under its button, right edges aligned. */}
      <Menu
        id="row-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{ paper: { sx: menuPaperStyles } }}
      >
        <MenuItem onClick={handleEdit} sx={menuItemStyles}>
          <ListItemIcon><PencilIcon size={16} /></ListItemIcon>
          {t('Common_Edit')}
        </MenuItem>
        <MenuItem onClick={handleSuspension} sx={menuItemStyles}>
          <ListItemIcon>
            {selectedUser?.isActive === false ? <UserCheckIcon size={16} /> : <UserXIcon size={16} />}
          </ListItemIcon>
          {t(selectedUser?.isActive === false ? 'User_Activate' : 'User_Suspend')}
        </MenuItem>
        {canManageSso && (selectedUser?.ssoLinked || selectedUser?.ssoLinkBlocked) && <Divider />}
        {canManageSso && selectedUser?.ssoLinked && (
          <MenuItem
            onClick={() => handleSsoAction((target) =>
              ssoActions.setManagement([target], target.managedBy === 'sso' ? 'local' : 'sso'))}
            sx={menuItemStyles}>
            <ListItemIcon><ArrowLeftRightIcon size={16} /></ListItemIcon>
            {t(selectedUser.managedBy === 'sso' ? 'User_SsoHandBack' : 'User_SsoHandOver')}
          </MenuItem>
        )}
        {canManageSso && selectedUser?.ssoLinked && (
          <MenuItem onClick={() => handleSsoAction(ssoActions.unlink)} sx={menuItemStyles}>
            <ListItemIcon><UnlinkIcon size={16} /></ListItemIcon>
            {t('User_SsoUnlink')}
          </MenuItem>
        )}
        {canManageSso && !selectedUser?.ssoLinked && selectedUser?.ssoLinkBlocked && (
          <MenuItem onClick={() => handleSsoAction(ssoActions.allowLink)} sx={menuItemStyles}>
            <ListItemIcon><LinkIcon size={16} /></ListItemIcon>
            {t('User_SsoAllowLink')}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleDelete} sx={[menuItemStyles, menuDangerItemStyles]}>
          <ListItemIcon><Trash2Icon size={16} /></ListItemIcon>
          {t('Common_Delete')}
        </MenuItem>
      </Menu>
      <UserFormModal isOpen={formModal} onClose={handleUserFormClose} selectedUser={selectedUser} />
      {deleteTarget && (
        <DeleteUserDialog
          key={deleteOpenings}
          open={dialogOpen}
          user={deleteTarget}
          onClose={() => setDialogOpen(false)}
          onExited={() => setDeleteTarget(null)}
        />
      )}
      <ConfirmDialog
        open={suspendOpen}
        title={t('User_Suspend_Title')}
        icon={<UserXIcon size={16} />}
        confirmLabel={t('User_Suspend')}
        confirmColor="warning"
        onClose={handleSuspendClose}
        onConfirm={async () => selectedUser
          ? !(await updateUser({ id: selectedUser.id, isActive: false })).error
          : false}
      >
        <Typography sx={formDialogTextStyles}>{t('User_Suspend_Content', [selectedUser?.name ?? ''])}</Typography>
      </ConfirmDialog>
      <ConfirmDialog
        open={bulkAction === 'suspend'}
        title={t('User_SuspendMany_Title', { count: bulkTargets.length })}
        icon={<UserXIcon size={16} />}
        confirmLabel={t('User_Suspend')}
        confirmColor="warning"
        onClose={() => setBulkAction(null)}
        onConfirm={suspendTargets}
      >
        <Typography sx={formDialogTextStyles}>{t('User_SuspendMany_Content')}</Typography>
      </ConfirmDialog>
      <DeleteUsersDialog
        open={bulkAction === 'delete'}
        users={bulkTargets}
        onClose={() => setBulkAction(null)}
        onConfirm={deleteTargets}
      />
      {ssoActions.dialogs}
    </div >
  );
};

/** As the row's menu looks: the card's colour, a thin edge and a shadow. */
const teamListStyles = {
  py: 0.75,
  minWidth: 220,
  backgroundImage: 'none',
  border: 1,
  borderColor: 'divider',
  boxShadow: colorSchemes.baseShadow,
};

const teamRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 3,
  height: 28,
  px: 1.5,
  fontSize: 13,
  color: tableText.primary,
};

const teamLinkStyles = {
  textDecoration: 'none',
  // The page's `a:hover` would turn the name purple.
  '&:hover': { backgroundColor: 'action.hover', color: tableText.primary },
};

const containerStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  width: '100%',
};

/**
 * As tall as the table's tools, in the words' own case, and in the record dialog's blue, a step
 * darker than the theme's so its white label keeps a contrast of 4.5 to 1.
 */
const newButtonStyles = {
  height: 32,
  px: 1.5,
  // On one line on a phone too: wrapped, its label was cut by the fixed height.
  flexShrink: 0,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  color: '#fff',
  backgroundColor: brand[500],
  '&:hover': { backgroundColor: brand[700] },
  '& .MuiButton-startIcon': { mr: 0.75 },
  '& .MuiButton-startIcon svg': { fontSize: 16 },
};

export default UsersPage;
